import moment from "../utilities/Moment";
import store from "../store";
import { Moment } from "moment";
import TimeFormatter from "../utilities/TimeFormatter";
import { TimeEntry } from "../types";
import { MomentRange } from "moment-range";
import { Style } from "../styles";

type FaviconState = "play" | "pause" | null;

const FAVICON_LINK_IDS = ["favicon-32x32", "favicon-16x16"];

export default class TimeHelper {
  private static defaultFaviconHrefs: Record<string, string> | null = null;
  private static currentFaviconState: FaviconState | undefined = undefined;

  static convertSecondsToHours(secondsCount) {
    return Number((secondsCount / 3600).toFixed(2));
  }

  static getCurrentTimeInSeconds() {
    const now = moment();
    const startOfDay = moment().startOf("day");

    return now.diff(startOfDay, "seconds");
  }

  static getNewTimeEntry(defaultRate: number): TimeEntry {
    return {
      description: "",
      contact_id: "",
      project_id: "",
      base_rate: defaultRate,
      billable: true,
      active: false,
      fixed_rate: false,
      started_at: moment().utc().toString(),
      ended_at: moment().utc().toString(),
    };
  }

  static getDateRange(startDate: Moment, stopDate: Moment): Moment[] {
    // @ts-ignore
    return Array.from(moment.range(startDate, stopDate).by("day"));
  }

  static getTimeEntryColor(timeEntry?: TimeEntry) {
    if (timeEntry) {
      if (timeEntry.project) {
        return timeEntry.project.color;
      } else if (timeEntry.contact) {
        return timeEntry.contact.color;
      }
    }

    return "#9b9b9b";
  }

  /**
   * Calculates the minutes of the day to compare hh:mm and see which one is bigger
   * @param test
   */
  static minutesOfDay(test: Moment) {
    return test.minutes() + test.hours() * 60;
  }

  static getTimerTitle() {
    const {
      timer: { entry },
    } = store.getState();

    if (entry) {
      const durationlabel = this.getTimerDuration();
      const descriptionLabel = this.getTimerDescription();

      if (durationlabel !== "" && descriptionLabel !== "") {
        return `${durationlabel} - ${descriptionLabel}`;
      }

      return durationlabel;
    }

    return "";
  }

  static setDocumentTimerTitle() {
    const timerTitle = this.getTimerTitle();

    // Only update if valid timer
    if (timerTitle) {
      document.title = timerTitle;
    }
  }

  static getTimerFaviconState(): FaviconState {
    const {
      timer: { entry },
    } = store.getState();

    if (!entry) {
      return null;
    }

    return entry.active ? "play" : "pause";
  }

  static setDocumentFavicon() {
    const state = this.getTimerFaviconState();

    // Skip when nothing changed to avoid redrawing the canvas on every tick
    if (state === this.currentFaviconState) {
      return;
    }

    this.currentFaviconState = state;

    const links = FAVICON_LINK_IDS.map(
      (id) => document.getElementById(id) as HTMLLinkElement | null
    ).filter((link): link is HTMLLinkElement => !!link);

    if (links.length === 0) {
      return;
    }

    if (!this.defaultFaviconHrefs) {
      this.defaultFaviconHrefs = {};
      links.forEach((link) => {
        this.defaultFaviconHrefs[link.id] = link.href;
      });
    }

    const dataUrl = state ? this.generateFaviconDataUrl(state) : null;

    links.forEach((link) => {
      link.href = dataUrl || this.defaultFaviconHrefs[link.id];
    });
  }

  static generateFaviconDataUrl(state: "play" | "pause"): string {
    const size = 64;
    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext("2d");

    if (!ctx) {
      return "";
    }

    const color =
      state === "play" ? Style.color.brandSuccess : Style.color.brandWarning;

    ctx.beginPath();
    ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
    ctx.fillStyle = color;
    ctx.fill();

    ctx.fillStyle = Style.color.white;

    if (state === "play") {
      ctx.beginPath();
      ctx.moveTo(size * 0.38, size * 0.26);
      ctx.lineTo(size * 0.38, size * 0.74);
      ctx.lineTo(size * 0.78, size * 0.5);
      ctx.closePath();
      ctx.fill();
    } else {
      const barWidth = size * 0.14;
      const barHeight = size * 0.4;
      const gap = size * 0.14;
      const top = (size - barHeight) / 2;

      ctx.fillRect(size / 2 - gap / 2 - barWidth, top, barWidth, barHeight);
      ctx.fillRect(size / 2 + gap / 2, top, barWidth, barHeight);
    }

    return canvas.toDataURL("image/png");
  }

  static getTimerDuration() {
    const {
      authentication: {
        currentUser: {
          workspace: { setting },
        },
      },
      timer: { entry },
    } = store.getState();

    let durationInSeconds = 0;
    if (entry) {
      durationInSeconds = moment
        .duration(moment().diff(moment(entry.started_at)))
        .asSeconds();
    }

    // Only trim when hh mm ss format
    let trim = setting.time_format === "hh mm ss";

    return TimeFormatter.durationFormat(
      durationInSeconds,
      setting.time_format,
      trim
    );
  }

  static getTimerDescription() {
    const {
      timer: { entry },
    } = store.getState();
    if (entry) {
      const { contact, project } = entry;

      if (contact && project) {
        return `${contact.name} › ${project.name}`;
      } else if (contact) {
        return `${contact.name}`;
      } else if (project) {
        return `${project.name}`;
      } else if (entry.description) {
        return entry.description;
      }
    }

    return "";
  }

  static getYearRange(startYear: number) {
    let years = [];
    let currentYear = new Date().getFullYear();
    startYear = startYear || 1980;
    while (startYear <= currentYear) {
      years.push(startYear++);
    }
    return years.reverse();
  }
}
